'use client';

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiArrowLeftLine,
  RiErrorWarningLine,
  RiEyeLine,
  RiEyeOffLine,
  RiLoader4Line,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { Logo } from '@/components/logo';
import { Link } from '@/i18n/navigation';
import { dur, ease, spring, springFade } from '@/lib/motion';
import { siteLinks } from '@/lib/site-links';

/**
 * Giriş / kayıt / şifre sıfırlama ekranlarının ortak parçaları.
 *
 * Neden ayrı bir dosya: üç formun da alan kutusu, hata satırı, gönder butonu ve
 * Google yolu AYNI. Üç kopya tutulsaydı bir davranışı düzeltmek üç dosyada
 * düzeltmek olurdu — ve kaçınılmaz olarak biri unutulurdu. Buradaki parçalar
 * durum tutmuyor (göz düğmesi ve odak bayrağı dışında); doğrulama, bekleme ve
 * hata metinleri her zaman çağıran formun elinde kalıyor.
 *
 * Görünüm CSS'te: `globals.css` → "Giriş ekranı" bloğu, `.auth-*` sınıfları.
 * Buradaki bileşenler sınıf adı basar, ölçü yazmaz.
 */

/**
 * `layout` taşıyan öğelerin giriş varyantı — sahne girişinin y ötelemesiz hâli.
 *
 * Neden ayrı: bir öğede hem `layout` hem de y ötelemeli giriş çalıştırmak,
 * Motion'ın ölçüm aldığı ana denk gelirse öğeyi sıçratıyor. Hata satırı
 * belirdiğinde alanları AŞAĞI İTEN hareket zaten `layout`ta; girişin ayrıca yol
 * alması gerekmiyor, "odaklanma" yeterli.
 */
export const fieldV: Variants = {
  hidden: { opacity: 0, filter: 'blur(5px)' },
  show: { opacity: 1, filter: 'blur(0px)', transition: { duration: dur.base, ease: ease.out } },
};

/* Hata satırı: girerken blur'lu değil — 12px'lik yolu da yok. Uyarı metni
   "odaklanarak" gelmemeli, anında okunmalı. */
export const errorV: Variants = {
  hidden: { opacity: 0, y: -4 },
  show: { opacity: 1, y: 0, transition: { duration: dur.fast, ease: ease.out } },
  exit: { opacity: 0, y: -4, transition: { duration: dur.instant } },
};

/**
 * Logo + geri oku. Tek bağlantı iki iş yapıyor: marka ve "siteye dön".
 * Ok logonun SOLUNDA — hover'da sola kayarak gideceği yönü gösteriyor
 * (kayma CSS'te, `.auth-back:hover .auth-back-arrow`).
 */
export function AuthBrand({ href = siteLinks.home.href }: { href?: string }) {
  return (
    <div className="auth-brand">
      <Link href={href} className="auth-back">
        <RiArrowLeftLine className="auth-back-arrow" size={16} aria-hidden="true" />
        <Logo className="auth-logo" />
      </Link>
    </div>
  );
}

/**
 * Hızlı yol. Formun ÜSTÜNDE duruyor: Google ile geçen kullanıcı alanlara hiç
 * bakmadan geçsin. Altta olsaydı önce alanları okuyup sonra "aslında daha kısası
 * varmış" demek zorunda kalırdı.
 */
export function GoogleButton({ label }: { label: string }) {
  return (
    <motion.button
      type="button"
      className="auth-social"
      whileHover={{ scale: 1.01, y: -1 }}
      whileTap={{ scale: 0.99 }}
      transition={spring.snap}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/logos/brands/google.svg" alt="" className="auth-social-logo" />
      {label}
    </motion.button>
  );
}

export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="auth-divider">
      <span className="auth-divider-line" aria-hidden="true" />
      <span className="auth-divider-text">{label}</span>
      <span className="auth-divider-line" aria-hidden="true" />
    </div>
  );
}

/**
 * Alan kutusu: ikon + girdi + (varsa) sağdaki düğme.
 *
 * Odakta değişen tek şey kutunun kendisi — kenarlık tema rengine dönüyor,
 * zemindeki yeşil sis koyulaşıyor. Dışarıda ayrı bir odak halkası, altta ayrı
 * bir çizgi yok; üçü aynı anda "buradasın" deyince kutu üç kat kalınlaşmış gibi
 * görünüyordu. Renk geçişleri CSS'te, düzen ölçmüyorlar.
 *
 * `:focus-within` yerine state: ikon rengi de aynı bayrağa bağlı ve tek
 * kaynaktan okunuyor.
 */
export function AuthField({
  icon,
  invalid,
  children,
}: {
  icon: ReactNode;
  invalid: boolean;
  children: ReactNode;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div
      className="auth-input-wrap"
      data-invalid={invalid || undefined}
      data-focused={focused || undefined}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <span className="auth-input-icon" aria-hidden="true">
        {icon}
      </span>
      {children}
    </div>
  );
}

/**
 * Şifre alanının göz düğmesi. İkon yerinde DEĞİŞİYOR, iki ikon üst üste
 * durmuyor: `mode="wait"` çıkanı bitmeden yenisini basmıyor. Dönüş küçük
 * (−90°) — göz kırpması gibi, takla değil.
 */
export function PasswordReveal({
  revealed,
  onToggle,
  showLabel,
  hideLabel,
}: {
  revealed: boolean;
  onToggle: () => void;
  showLabel: string;
  hideLabel: string;
}) {
  return (
    <button
      type="button"
      className="auth-reveal"
      onClick={onToggle}
      aria-label={revealed ? hideLabel : showLabel}
      aria-pressed={revealed}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={revealed ? 'on' : 'off'}
          className="auth-reveal-icon"
          initial={{ opacity: 0, rotate: -90, scale: 0.7 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 90, scale: 0.7 }}
          transition={springFade(spring.snap, dur.instant)}
        >
          {revealed ? <RiEyeOffLine size={18} /> : <RiEyeLine size={18} />}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

/** Alan altı hata satırı. Yer kaplamayan bir kutu değil — yokken hiç yok,
    varken altındakileri `layout` ile aşağı itiyor. */
export function FieldError({ id, message }: { id: string; message?: string }) {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      {message && (
        <motion.p
          key="error"
          id={id}
          className="auth-field-error"
          variants={errorV}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          {message}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

/**
 * Form geneli hata. Sarmalayıcı kutu YOK: yokken hiç yer kaplamasın (boş bir
 * div bile flex `gap`ini tüketip çift boşluk bırakıyordu), AnimatePresence'ın
 * kendisi DOM'a düğüm basmıyor.
 *
 * `role="alert"` — `aria-live` değil: canlı bölge, içeriği değişmeden ÖNCE
 * DOM'da olmak zorunda; burada eleman sonradan basılıyor. `alert` eklendiği an
 * okunuyor. Alan hataları ayrıca canlı yapılmadı, onlar `aria-describedby` ile
 * alana bağlı.
 */
export function FormAlert({ message }: { message?: string }) {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      {message && (
        <motion.p
          key="form-error"
          role="alert"
          className="auth-alert"
          variants={errorV}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          <RiErrorWarningLine size={16} aria-hidden="true" />
          {message}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

/**
 * Gönder butonu.
 *
 * 🔑 İçindeki bekleme çarkı sonsuz döngü: `useReducedMotion()` ile elle
 * kapatılıyor. MotionConfig'in `reducedMotion="user"` ayarı döngüleri
 * durdurmuyor (DESIGN.md §1). Tercih açıkken çark sabit duruyor; "bekleniyor"
 * bilgisini zaten butonun metni ve `aria-busy` taşıyor.
 */
export function SubmitButton({
  pending,
  label,
  pendingLabel,
}: {
  pending: boolean;
  label: string;
  pendingLabel: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.button
      type="submit"
      className="auth-submit"
      disabled={pending}
      aria-busy={pending}
      whileHover={pending ? undefined : { scale: 1.01, y: -1 }}
      whileTap={pending ? undefined : { scale: 0.985 }}
      transition={spring.snap}
    >
      {/* Metin de ikon da yerinde değişiyor; buton genişliği sabit kalsın diye
          ikisi de aynı satırda, `mode="wait"` ile. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={pending ? 'pending' : 'idle'}
          className="auth-submit-label"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: dur.instant, ease: ease.out }}
        >
          {pending && (
            <motion.span
              className="auth-submit-spinner"
              aria-hidden="true"
              animate={reduceMotion ? undefined : { rotate: 360 }}
              transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
            >
              <RiLoader4Line size={18} />
            </motion.span>
          )}
          {pending ? pendingLabel : label}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
}

/* ==========================================================================
   Şifre gücü ölçeri — kayıt ve şifre sıfırlama ekranlarında ortak
   ========================================================================== */

/** Sunucu daha uzununu isteyebilir; bu alt sınır, hedef değil. */
export const MIN_PASSWORD = 8;

/**
 * Şifre gücü: 0–4.
 *
 * Neden entropi hesabı değil: gerçek bir tahmin (zxcvbn ve benzeri) 400 KB'lık
 * sözlük demek — bir kayıt ekranının bütün JS bütçesinden büyük. Buradaki ölçek
 * kullanıcıya "daha uzun ve daha çeşitli yaz" demekten fazlasını iddia etmiyor;
 * şifrenin kabul edilip edilmeyeceğine sunucu karar veriyor.
 *
 * `hint` — eksik olan İLK şey. Aynı anda üç öğüt vermek hiçbirini okutmuyor;
 * sıradaki tek adımı söylemek okutuyor.
 */
function measure(value: string): { score: 0 | 1 | 2 | 3 | 4; hint?: 'length' | 'number' | 'variety' } {
  /* 0 yalnızca DOKUNULMAMIŞ alan demek — ölçer o zaman hiçbir şey söylemiyor. */
  if (value.length === 0) return { score: 0 };

  const long = value.length >= MIN_PASSWORD;
  const longer = value.length >= 12;
  const number = /\d/.test(value);
  const variety =
    /[^A-Za-z0-9]/.test(value) || (/[a-zçğıöşü]/.test(value) && /[A-ZÇĞİÖŞÜ]/.test(value));

  /* Taban 1: hiçbir ölçütü tutmayan bir şifre de "zayıf" diye ölçülmeli.
     Sıfır bırakılsaydı kullanıcı yazdıkça ölçer boş kalırdı — dokunulmamış
     alanla altı harflik kötü bir şifre aynı görünürdü. */
  const met = Number(long) + Number(longer) + Number(number) + Number(variety);
  const score = Math.max(1, met) as 1 | 2 | 3 | 4;

  const hint = !long ? 'length' : !number ? 'number' : !variety ? 'variety' : undefined;
  return { score, hint };
}

/** 0 = boş alan, etiket basılmıyor. */
const LEVELS = ['', 'weak', 'fair', 'good', 'strong'] as const;

/**
 * Dört bölme + tek satır metin.
 *
 * ── Neden dört bölme, tek çubuk değil ─────────────────────────────────────
 * Tek çubuğun uzunluğu "ne kadar kaldı" sorusunu doğuruyor; oysa hedef bir yüzde
 * değil. Bölmeler kademe okutuyor: dolan bölme sayısı arttıkça güç arttı, o
 * kadar.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 * Dolgu `scaleX` ile, `transform-origin: left` — genişlik animasyonu her karede
 * yeniden düzen demek (DESIGN.md §1). Soldan sağa dolması yönü anlatıyor: ölçek
 * arttı, bir şey yanıp sönmedi.
 *
 * Metin `mode="wait"` ile yerinde değişiyor. Ölçek zaten "arttı" diyor; metin ne
 * kadar arttığını ve sıradaki tek adımı söylüyor — renk tek başına bilgi
 * taşımıyor (DESIGN.md §1).
 *
 * Metinler `auth.strength` altında, kayıt ve sıfırlama ekranlarına ait
 * namespace'lerde DEĞİL: aynı ölçek iki yerde de aynı şeyi söylüyor.
 */
export function PasswordStrength({ id, value }: { id: string; value: string }) {
  const t = useTranslations('auth.strength');
  const { score, hint } = useMemo(() => measure(value), [value]);
  const label = score > 0 ? t(`levels.${LEVELS[score]}`) : '';

  return (
    <div className="auth-strength" data-score={score} id={id}>
      <div className="auth-strength-track" aria-hidden="true">
        {[1, 2, 3, 4].map((step) => (
          <span key={step} className="auth-strength-slot">
            <motion.span
              className="auth-strength-fill"
              initial={false}
              animate={{ scaleX: score >= step ? 1 : 0 }}
              transition={spring.snap}
            />
          </span>
        ))}
      </div>

      {/* Yükseklik sabit (CSS'te `min-height`): metin gelip gittikçe altındaki
          buton zıplamasın. Boşken hiçbir şey yazmıyor — dokunulmamış bir alanın
          altında "çok zayıf" yazması azar gibi duruyordu. */}
      <p className="auth-strength-text">
        <AnimatePresence mode="wait" initial={false}>
          {label && (
            <motion.span
              key={label}
              className="auth-strength-level"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: dur.instant, ease: ease.out }}
            >
              {label}
              {hint && <span className="auth-strength-hint"> · {t(`hints.${hint}`)}</span>}
            </motion.span>
          )}
        </AnimatePresence>
      </p>
    </div>
  );
}

/**
 * Yasal satırın bağlantı üreticileri — `t.rich('legal', legalChunks)`.
 *
 * Metin her formda farklı ("giriş yaparak…" / "hesap oluşturarak…"), gidilen
 * iki adres aynı. Ortak olan burada duruyor, değişen çeviri dosyasında.
 */
export const legalChunks = {
  terms: (chunks: ReactNode) => (
    <Link href="/terms" className="auth-legal-link">
      {chunks}
    </Link>
  ),
  privacy: (chunks: ReactNode) => (
    <Link href="/privacy" className="auth-legal-link">
      {chunks}
    </Link>
  ),
};
